import { kokoroClient } from './kokoroClient';
import { voiceBundle } from './voiceBundle';
import { resolveKokoroSpec } from './kokoroSpec';
import { resolveBlendedStyle } from './styleVector';
import { buildArcTable } from './arcProperties';
import { phonemizeForKokoro } from './phonemize';
import { applyEnergyScale, applyPitchScale } from './postProcess';
import { uploadKokoroWav } from './uploadKokoroWav';
import { ensureXxhash, parseHexSeed } from './xxhash';
import { KOKORO_VOICES } from './voiceMapping';
import type { KokoroVoiceId } from './voiceMapping';
import type {
  ArcProperties,
  KokoroAgentGender,
  KokoroGender,
  KokoroSpeakResult,
  VoiceSpec,
} from './types';

let cachedArcTable: ArcProperties[][] | null = null;
let cachedArcSeed: bigint | null = null;

export function invalidateArcTable(): void {
  cachedArcTable = null;
  cachedArcSeed = null;
}

async function getArcTable(worldSeed: bigint): Promise<ArcProperties[][]> {
  if (cachedArcTable && cachedArcSeed === worldSeed) return cachedArcTable;
  await voiceBundle.ensureReady();
  cachedArcTable = buildArcTable(worldSeed);
  cachedArcSeed = worldSeed;
  return cachedArcTable;
}

export interface SpeakBlendInput {
  text: string;
  turnId: string | null;
  agentName: string;
  agentGender: KokoroAgentGender;
  defaultGender: KokoroGender;
  pinnedSpec: VoiceSpec | null;
  worldSeedHex: string;
  /** Stage 12 (E2): optional per-turn mood point; when present, the
   *  spec's deterministic pitch/energy are gently modulated. */
  moodModulation?: { valence: number; arousal: number } | null;
}

/**
 * Stage 12 (E2): gentle mood → pitch/energy modulation layered on top of
 * the hash-derived deterministic spec values. Kept conservative so the
 * voice identity stays recognisable — ±7% pitch and ±18% energy at
 * extreme mood points. Returns a NEW spec; the original is unchanged.
 */
function applyMoodModulation(
  spec: VoiceSpec,
  mood: { valence: number; arousal: number } | null | undefined,
): VoiceSpec {
  if (!mood) return spec;
  const v = Math.max(-1, Math.min(1, mood.valence));
  const a = Math.max(-1, Math.min(1, mood.arousal));
  const pitchMod = 1 + v * 0.05 + a * 0.02;
  const energyMod = 1 + v * 0.08 + a * 0.1;
  return {
    ...spec,
    pitch: Math.max(0.8, Math.min(1.2, spec.pitch * pitchMod)),
    energy: Math.max(0.7, Math.min(1.3, spec.energy * energyMod)),
  };
}

interface InputIdsTensor {
  dims: number[];
}

export async function speakKokoroBlend(
  input: SpeakBlendInput,
): Promise<KokoroSpeakResult> {
  await ensureXxhash();
  const worldSeed = parseHexSeed(input.worldSeedHex || '0');
  const gender: KokoroGender =
    input.agentGender === 'default' ? input.defaultGender : input.agentGender;

  const baseSpec: VoiceSpec =
    input.pinnedSpec ??
    resolveKokoroSpec({ name: input.agentName, gender, worldSeed });
  // Stage 12 (E2): mood-aware modulation. Safe no-op when mood is absent.
  const spec: VoiceSpec = applyMoodModulation(
    baseSpec,
    input.moodModulation ?? null,
  );

  const [, arcTable] = await Promise.all([
    kokoroClient.ensureReady(),
    getArcTable(worldSeed),
  ]);

  const voiceAId = KOKORO_VOICES[spec.voiceA] as KokoroVoiceId;

  // 1. Phonemize.
  const phonemes = await phonemizeForKokoro(input.text, voiceAId);
  // eslint-disable-next-line no-console
  console.info(
    `[kokoro-blend] agent=${input.agentName} spec=A${spec.voiceA}(${KOKORO_VOICES[spec.voiceA]})×B${spec.voiceB}(${KOKORO_VOICES[spec.voiceB]})@${spec.t.toFixed(3)} p=${spec.pitch.toFixed(2)} e=${spec.energy.toFixed(2)} phonemes=${JSON.stringify(phonemes)}`,
  );

  const tts = kokoroClient.getTts();
  if (!tts) throw new Error('Kokoro not initialised');

  // 2. Tokenize.
  const tokenised = tts.tokenizer(phonemes, { truncation: true }) as {
    input_ids: InputIdsTensor;
  };
  const { input_ids } = tokenised;
  const dims = input_ids.dims;
  const tokenCount = dims[dims.length - 1] ?? 0;

  // 3. Style vector via arc-warped slerp.
  const arc = arcTable[spec.voiceA][spec.voiceB];
  const style = resolveBlendedStyle(spec, arc, tokenCount);

  // 4. Model forward.
  const transformers = (await import('@huggingface/transformers')) as unknown as {
    Tensor: new (
      dtype: string,
      data: Float32Array | number[],
      dims: number[],
    ) => unknown;
    RawAudio: new (audio: Float32Array, sampling_rate: number) => {
      toBlob: () => Blob;
    };
  };
  const styleTensor = new transformers.Tensor('float32', style, [1, 256]);
  const speedTensor = new transformers.Tensor('float32', [1.0], [1]);
  const modelOut = (await tts.model({
    input_ids,
    style: styleTensor,
    speed: speedTensor,
  })) as { waveform: { data: Float32Array } };

  // 5. Energy + pitch post-processing.
  const pcm = new Float32Array(modelOut.waveform.data);
  applyEnergyScale(pcm, spec.energy);
  const shifted = applyPitchScale(pcm, spec.pitch);

  // 6. WAV encode via transformers.js RawAudio.
  const raw = new transformers.RawAudio(shifted, 24000);
  const wav = raw.toBlob();
  const durationMs = Math.round((shifted.length / 24000) * 1000);

  // 7. Upload + return.
  return uploadKokoroWav({
    wav,
    voiceId: voiceAId,
    text: input.text,
    turnId: input.turnId,
    durationMs,
    sizeBytes: wav.size,
    agentName: input.agentName,
  });
}

/**
 * Local-only blend synthesis for the Settings test bench. Runs the same
 * pipeline as `speakKokoroBlend` but skips the R2 upload — returns a WAV
 * Blob the caller can play via URL.createObjectURL.
 */
export async function synthesizeBlendLocal(opts: {
  text: string;
  spec: VoiceSpec;
  worldSeedHex: string;
}): Promise<{ wav: Blob; durationMs: number }> {
  await ensureXxhash();
  const worldSeed = parseHexSeed(opts.worldSeedHex || '0');
  const [, arcTable] = await Promise.all([
    kokoroClient.ensureReady(),
    getArcTable(worldSeed),
  ]);

  const voiceAId = KOKORO_VOICES[opts.spec.voiceA] as KokoroVoiceId;
  const phonemes = await phonemizeForKokoro(opts.text, voiceAId);

  const tts = kokoroClient.getTts();
  if (!tts) throw new Error('Kokoro not initialised');

  const tokenised = tts.tokenizer(phonemes, { truncation: true }) as {
    input_ids: InputIdsTensor;
  };
  const { input_ids } = tokenised;
  const dims = input_ids.dims;
  const tokenCount = dims[dims.length - 1] ?? 0;

  const arc = arcTable[opts.spec.voiceA][opts.spec.voiceB];
  const style = resolveBlendedStyle(opts.spec, arc, tokenCount);

  const transformers = (await import('@huggingface/transformers')) as unknown as {
    Tensor: new (
      dtype: string,
      data: Float32Array | number[],
      dims: number[],
    ) => unknown;
    RawAudio: new (audio: Float32Array, sampling_rate: number) => {
      toBlob: () => Blob;
    };
  };
  const styleTensor = new transformers.Tensor('float32', style, [1, 256]);
  const speedTensor = new transformers.Tensor('float32', [1.0], [1]);
  const modelOut = (await tts.model({
    input_ids,
    style: styleTensor,
    speed: speedTensor,
  })) as { waveform: { data: Float32Array } };

  const pcm = new Float32Array(modelOut.waveform.data);
  applyEnergyScale(pcm, opts.spec.energy);
  const shifted = applyPitchScale(pcm, opts.spec.pitch);

  const raw = new transformers.RawAudio(shifted, 24000);
  return {
    wav: raw.toBlob(),
    durationMs: Math.round((shifted.length / 24000) * 1000),
  };
}
